/**
 * ============================================================================
 * DIE ZWEI RÜCKFRAGEN NACH EINER TERMINBUCHUNG.
 * ============================================================================
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „wir brauchen bei uns diese terminsteuerung wieder. das hatte alles was
 *    wir brauchen. und dann wenn ich den termin gebucht/geändert/storniert
 *    habe kamen noch die abfrag ob wir das gewerk per push in telegram mit dem
 *    text informieren wollen und danach noch die meldung ob wir den kunden
 *    informieren wollen per mail. also von dort gingen dann auch die
 *    terminbestätigungen raus."
 *
 * DIE REIHENFOLGE IST DIE DES BESTANDS, und sie ist nicht beliebig: erst die
 * Firma, dann der Kunde. Wer den Kunden zuerst bestätigt und danach merkt,
 * dass die Firma gar keinen Chat hat, hat einen Termin zugesagt, von dem
 * niemand weiss.
 *
 * JEDE FRAGE IST EINZELN ABWÄHLBAR, und „Nein" ist kein Fehler. Der Bestand
 * hält es genauso; es gibt Termine, die telefonisch längst besprochen sind.
 *
 * WAS HIER PASSIERT, IST NUR DER ABLAUF. Gesendet wird über die Wege, die es
 * dafür gibt - `/telegram` und `/terminmail` -, mit ihren eigenen Vorschauen,
 * ihren eigenen Riegeln und dem Testmodus davor. Eine dritte Sendestelle wäre
 * eine dritte, an der der Testmodus vergessen wird.
 *
 * UND DIE MAILADRESSE LÄSST SICH HIER PRÜFEN UND ÄNDERN. Wörtlich der
 * Bestand: „E-Mail-Adresse pruefbar und direkt aenderbar; die Aenderung wird
 * in die Auftragszeile geschrieben." Wer sie nur für diese eine Mail benutzt,
 * tippt sie beim nächsten Mal wieder.
 */
import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { Dialog, Fehlerkasten, ausFehler } from './Formular.tsx';

const GEWERK_TEXT: Record<string, string> = {
  TB: 'Tiefbau', EB: 'Einblasen', SP: 'Spleißen', DOKU: 'Dokumentation',
};

export interface Nachfrage {
  telegram: { gewerk: string; aktion: string; datum: string;
              von: string; bis: string }[];
  kundenmail: { gewerk: string; art: string;
                altDatum: string; altUhrzeit: string };
  /** Beim Storno aus dem Terminplaner: der Auftrag, zu dem gefragt wird. */
  auftragId?: string;
}

/**
 * Der Ablauf. Er zeigt IMMER genau eine Frage; ist sie beantwortet, kommt die
 * nächste, und am Ende ruft er `fertig`.
 *
 * Ein Schritt, für den es nichts zu fragen gibt (keine Firma mit Chat, keine
 * Mailadresse), wird ÜBERSPRUNGEN und sagt es in der Zusammenfassung. Eine
 * Frage, deren Antwort nichts bewirken kann, ist ein Klick zu viel.
 */
export default function Nachfragen(
  { auftragId, nachfrage, baustelle, fertig }: {
    auftragId: string;
    nachfrage: Nachfrage;
    baustelle?: string;
    fertig: (satz: string | null) => void;
  }
) {
  const [schritt, setSchritt] = useState<'telegram' | 'mail'>('telegram');
  const [getan, setGetan] = useState<string[]>([]);

  if (schritt === 'telegram') {
    return (
      <TelegramFrage
        auftragId={auftragId} nachfrage={nachfrage} baustelle={baustelle}
        weiter={(satz) => {
          if (satz) setGetan((g) => [...g, satz]);
          setSchritt('mail');
        }} />
    );
  }
  return (
    <MailFrage
      auftragId={auftragId} nachfrage={nachfrage} baustelle={baustelle}
      weiter={(satz) => {
        const alle = satz ? [...getan, satz] : getan;
        fertig(alle.length ? alle.join(' ') : null);
      }} />
  );
}


/** Erste Frage: die Firma per Telegram. */
function TelegramFrage(
  { auftragId, nachfrage, baustelle, weiter }: any
) {
  const [v, setV] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<any>(null);

  const arten: string[] = (nachfrage.telegram ?? []).map((t: any) => t.gewerk);
  const neu = (nachfrage.telegram ?? []).every((t: any) => t.aktion === 'neu');

  useEffect(() => {
    if (!arten.length) { weiter(null); return; }
    api.telegramAuftrag(auftragId, {
      anlass: neu ? 'neu' : 'update', gewerke: arten, senden: false,
    }).then((r) => {
      /* NICHTS ZU FRAGEN HEISST NICHT FRAGEN. Hängt an keinem der Gewerke
         eine Firma mit Chat, kann diese Frage nichts bewirken. */
      if (!(r.eintraege ?? []).some((e: any) => !e.grund)) {
        weiter(null);
        return;
      }
      setV(r);
    }).catch(() => weiter(null));
  }, []);

  if (!v) return null;

  const geht = (v.eintraege ?? []).filter((e: any) => !e.grund);
  return (
    <Dialog titel="Firma informieren?" untertitel={baustelle}
            laeuft={laeuft} zu={() => weiter(null)} breite={620}>
      <Fehlerkasten lage={fehler} />
      <p className="klein">
        Soll{' '}
        <b>{[...new Set(geht.map((e: any) => e.firma))].join(', ')}</b>
        {' '}über {neu ? 'den neuen Termin' : 'die Änderung'} für{' '}
        <b>{arten.map((a) => GEWERK_TEXT[a] ?? a).join(' + ')}</b>
        {' '}per Telegram informiert werden?
      </p>

      {/* GENAU DAS GEHT HINAUS. Dieselbe Vorschau wie im Nachrichtenfenster -
          es ist derselbe Weg mit `senden: false`. */}
      {geht.map((e: any, i: number) => (
        <div key={i} className="tgvorschau">
          <div className="klein"><b>{e.firma}</b></div>
          <pre className="tgtext">{e.text}</pre>
        </div>
      ))}
      {(v.eintraege ?? []).filter((e: any) => e.grund).map((e: any, i: number) => (
        <p key={'x' + i} className="klein leise">{e.grund}</p>
      ))}
      {v.testmodus && (
        <div className="hinweis warnung">
          Der Testmodus ist an. Es geht nichts hinaus; der Versuch steht im
          Protokoll.
        </div>
      )}

      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt" disabled={laeuft}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    const r = await api.telegramAuftrag(auftragId, {
                      anlass: neu ? 'neu' : 'update',
                      gewerke: arten, senden: true });
                    weiter(r.gesendet
                      ? `${r.gesendet} Telegram-Nachricht`
                        + `${r.gesendet === 1 ? '' : 'en'} verschickt.`
                      : 'Telegram ging nicht hinaus.');
                  } catch (f) { setFehler(ausFehler(f)); setLaeuft(false); }
                }}>
          {laeuft ? 'Einen Moment' : 'Ja, per Telegram'}
        </button>
        <button className="knopf" disabled={laeuft}
                onClick={() => weiter(null)}>Nein</button>
      </div>
    </Dialog>
  );
}


/** Zweite Frage: der Kunde per Mail. */
function MailFrage({ auftragId, nachfrage, baustelle, weiter }: any) {
  const [v, setV] = useState<any>(null);
  const [an, setAn] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<any>(null);

  const km = nachfrage.kundenmail ?? null;

  useEffect(() => {
    if (!km) { weiter(null); return; }
    api.terminmail(auftragId, {
      art: km.art, gewerk: km.gewerk,
      altDatum: km.altDatum, altUhrzeit: km.altUhrzeit, senden: false,
    }).then((r) => {
      setV(r); setAn(r.an ?? '');
    }).catch(() => weiter(null));
  }, []);

  if (!v) return null;

  /* WAS NICHT GEHT, SAGT DAS FENSTER UND FRAGT NICHT.
     „Die Terminmails sind abgeschaltet" und „keine Mailadresse" sind zwei
     verschiedene nächste Schritte - und beide sind eine Auskunft und kein
     Fehler. Ein Fenster mit einem grauen Knopf ohne Grund sieht aus wie ein
     kaputter Knopf. */
  if (v.fehlt && !v.an) {
    return (
      <Dialog titel="Kunde informieren?" untertitel={baustelle}
              zu={() => weiter(null)} breite={560}>
        <div className="hinweis warnung">{v.fehlt}</div>
        <div className="reihe" style={{ marginTop: 12 }}>
          <button className="knopf haupt" onClick={() => weiter(null)}>
            Verstanden
          </button>
        </div>
      </Dialog>
    );
  }

  const gueltig = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(an);
  return (
    <Dialog titel="Kunde informieren?" untertitel={baustelle}
            laeuft={laeuft} zu={() => weiter(null)} breite={620}>
      <Fehlerkasten lage={fehler} />
      <p className="klein">
        Kunde <b>{v.kunde || '(ohne Namen)'}</b>:{' '}
        {km.art === 'umbuchung' ? 'Verschiebung' : km.art === 'absage' ? 'Absage' : 'Bestätigung'} des{' '}
        <b>{v.gewerk}</b>-Termins.
      </p>

      <div className="feld breit">
        <label className="klein" htmlFor="nf_an">E-Mail-Adresse des Kunden</label>
        <input id="nf_an" value={an} onChange={(e) => setAn(e.target.value)} />
        <span className="feldhinweis">
          Eine Änderung wird in den Auftrag übernommen.
        </span>
      </div>

      <div className="feld breit">
        <label className="klein">Betreff</label>
        <input value={v.betreff ?? ''} readOnly />
      </div>
      <div className="feld breit">
        <label className="klein">Das geht hinaus</label>
        <textarea rows={10} value={v.text ?? ''} readOnly />
      </div>
      {!!(v.leer ?? []).length && (
        <p className="klein leise">
          Ohne Wert geblieben: {v.leer.join(', ')}. Die Stelle bleibt leer.
        </p>
      )}
      {v.fehlt && <div className="hinweis warnung">{v.fehlt}</div>}

      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt" disabled={laeuft || !gueltig || !!v.fehlt}
                title={!gueltig ? 'Das sieht nicht nach einer Adresse aus.'
                                : (v.fehlt ?? undefined)}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    const r = await api.terminmail(auftragId, {
                      art: km.art, gewerk: km.gewerk, an,
                      altDatum: km.altDatum, altUhrzeit: km.altUhrzeit,
                      senden: true });
                    weiter(r.gesendet
                      ? `Kundenmail an ${r.an} verschickt.`
                      : 'Die Kundenmail ging nicht hinaus.');
                  } catch (f) { setFehler(ausFehler(f)); setLaeuft(false); }
                }}>
          {laeuft ? 'Einen Moment' : 'Ja, senden'}
        </button>
        <button className="knopf" disabled={laeuft}
                onClick={() => weiter(null)}>Nein</button>
      </div>
    </Dialog>
  );
}
